import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../config";

// GET: Fetch User Groups by User ID

export const fetchUserGroupsByUserId = createAsyncThunk(
  "userGroup/fetchByUserId",
  async (userId, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.get(
        `${API_BASE_URL}/api/Communication/UserGroup/User/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const userGroups = response.data;
      return userGroups;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch user groups"
      );
    }
  }
);

// POST: Create User Group
export const createUserGroup = createAsyncThunk(
  "userGroup/create",
  async (groupData, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const payload = {
        name: groupData.name,
        groupAdminUserID: groupData.groupAdminUserID,
        createAccess: groupData.createAccess,
        replyAccess: groupData.replyAccess,
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/Communication/UserGroup`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to create user group"
      );
    }
  }
);

// DELETE: Delete User Group
export const deleteUserGroup = createAsyncThunk(
  "userGroup/delete",
  async (id, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const response = await axios.delete(
        `${API_BASE_URL}/api/Communication/UserGroup/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return id; // Returning the ID to identify which group to remove
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete user group"
      );
    }
  }
);

// PUT: Update User Group Name
export const updateUserGroup = createAsyncThunk(
  "userGroup/update",
  async ({ id, groupData }, { rejectWithValue }) => {
    try {
      const storedUser = localStorage.getItem("userInfo");
      const { token } = JSON.parse(storedUser);

      const payload = {
        name: groupData.name,
        groupAdminUserID: groupData.groupAdminUserID,
        createAccess: groupData.createAccess,
        replyAccess: groupData.replyAccess,
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/Communication/UserGroup/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      return response.data; // Returning updated group data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to update user group"
      );
    }
  }
);

const userGroupSlice = createSlice({
  name: "userGroup",
  initialState: {
    userGroups: [],
    loading: false,
    error: null,
    modalVisible: false,
  },
  reducers: {
    openUserGroupModal(state) {
      state.modalVisible = true;
    },
    closeUserGroupModal(state) {
      state.modalVisible = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchUserGroupsByUserId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserGroupsByUserId.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroups = action.payload;
      })
      .addCase(fetchUserGroupsByUserId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createUserGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUserGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroups.push(action.payload);
        state.modalVisible = false;
      })
      .addCase(createUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteUserGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.userGroups = state.userGroups.filter(
          (group) => group.id !== action.payload
        );
      })
      .addCase(deleteUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateUserGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.userGroups.findIndex(
          (group) => group.id === action.payload.id
        );
        if (index !== -1) {
          state.userGroups[index] = action.payload;
        }
      })
      .addCase(updateUserGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { openUserGroupModal, closeUserGroupModal } =
  userGroupSlice.actions;
export default userGroupSlice.reducer;
